import i18n from '../index';

export interface CallDuration {
  /** On-screen form, e.g. "2 min 24 sec" (en) or "2 min 24 s" (fr-CA). */
  short: string;
  /** Screen-reader form, e.g. "2 minutes, 24 seconds". */
  spoken: string;
}

const CALL_DURATION_UNITS = ['hour', 'minute', 'second'] as const;

type CallDurationUnit = (typeof CALL_DURATION_UNITS)[number];

const joinUnits = (
  locale: string,
  amounts: Record<CallDurationUnit, number>,
  unitDisplay: 'short' | 'long',
): string => {
  const units = CALL_DURATION_UNITS.filter((unit) => amounts[unit] > 0);
  const parts = (units.length > 0 ? units : (['second'] as const)).map((unit) =>
    new Intl.NumberFormat(locale, {
      style: 'unit',
      unit,
      unitDisplay,
    }).format(amounts[unit]),
  );
  // Narrow unit lists join with spaces ("2 min 24 sec"); the short style
  // would add commas that read oddly next to the meta-line dots.
  return new Intl.ListFormat(locale, {
    type: 'unit',
    style: unitDisplay === 'short' ? 'narrow' : 'long',
  }).format(parts);
};

/** Call or recording length, rounded to whole seconds with zero units dropped. */
export const formatCallDuration = (totalSeconds: number): CallDuration => {
  const seconds = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.round(totalSeconds))
    : 0;
  const amounts = {
    hour: Math.floor(seconds / 3600),
    minute: Math.floor((seconds % 3600) / 60),
    second: seconds % 60,
  };
  const format = (locale: string): CallDuration => ({
    short: joinUnits(locale, amounts, 'short'),
    spoken: joinUnits(locale, amounts, 'long'),
  });

  try {
    return format(i18n.language || 'en');
  } catch {
    return format('en');
  }
};

/**
 * Elapsed time from an ISO timestamp to now, for queue metrics and similar UI.
 * Uses the queues namespace for hour/minute unit layout per locale.
 */
export const formatDurationFromTimestamp = (
  timestamp: string | null | undefined,
): string => {
  const format = (hours: number, minutes: number) =>
    i18n.t('queues:metrics.durationHoursMinutes', {
      hours,
      minutes,
      defaultValue: '{{hours}}h {{minutes}}m',
    });

  if (timestamp === null || timestamp === undefined || timestamp === '') {
    return format(0, 0);
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return format(0, 0);
  }
  const ms = Date.now() - date.getTime();
  if (ms <= 0) {
    return format(0, 0);
  }
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return format(hours, minutes);
};
